import { Environment, PRODUCT_READING, PRODUCT_LECTURA } from 'root/lib/constants';
import { localizedStrings } from 'lib/l10n/l10n';
import measureIds from 'a/placementTool/data/measureIds';
import GameCategories from './GameCategories';
import GameIds from './GameIds';

const ALL_ENVS = Object.keys(Environment).map(key => Environment[key]);

// optional keys defaults from this obj added to all the games' detail objs when exporting
const GAME_DEFAULTS = {
// array of environments the game should appear on. Default for reading is all environments, lectura is none
  release: {
    [PRODUCT_READING]: ALL_ENVS,
    [PRODUCT_LECTURA]: [],
  },
  excludeOnMobile: false,
  excludeOnNative: false,
  fieldTrialOnly: false,
  isPrototype: false,
  isMeasure: {
    [PRODUCT_READING]: false,
    [PRODUCT_LECTURA]: false,
  },
};

const gamesList = [
  {
    canonicalName: GameIds.ALL_ABOARD,
    categories: [GameCategories.PHONOLOGICAL_AWARENESS],
    studentSkill: 'Phonological Awareness',
    studentDescription: 'Practice segmenting phonemes and counting syllables that represent the number of sounds in a spoken word.',
    release: { [PRODUCT_LECTURA]: ALL_ENVS },
  },
  {
    canonicalName: GameIds.BECAUSE_THIS_THAT,
    categories: [GameCategories.MICROCOMPREHENSION],
    studentSkill: 'Common Text Structures',
    studentDescription: 'Learn how common text structures give clues to meaning by rearranging sentences to identify cause and effect or problem and solution.',
  },
  {
    canonicalName: GameIds.BEST_BUDDY,
    categories: [GameCategories.KEY_IDEAS_AND_DETAILS],
    studentSkill: 'Identify Character Traits',
    studentDescription: 'Examine character traits to determine which school club provides the best fit for their fictional friends.',
  },
  {
    canonicalName: GameIds.BOOK_CLUB,
    categories: [GameCategories.INTEGRATION_OF_KNOWLEDGE_AND_IDEAS],
    studentSkill: 'Compare & Contrast Texts',
    studentDescription: 'Compare and contrast two books on the same topic or theme to determine which book fits the character. ',
  },
  {
    canonicalName: GameIds.CITY_PLANNER,
    categories: [GameCategories.INTEGRATION_OF_KNOWLEDGE_AND_IDEAS],
    studentSkill: 'Compare Two Texts',
    studentDescription: 'Practice integrating the knowledge and ideas of texts by synthesizing the viewpoints and data presented at a town hall meeting.',
    fieldTrialOnly: true,
  },
  {
    canonicalName: GameIds.CODEX,
    categories: [GameCategories.MICROCOMPREHENSION],
    studentSkill: 'Figurative Language',
    studentDescription: 'Practice deciphering similes, metaphors, personification and other descriptive language in text.',
    fieldTrialOnly: true,
  },
  {
    canonicalName: GameIds.CONNECT_IT,
    categories: [GameCategories.MICROCOMPREHENSION],
    studentSkill: 'Connectives',
    studentDescription: 'Use different types of conjunctions to combine two clauses into a coherent sentence.',
    release: { [PRODUCT_LECTURA]: ALL_ENVS },
  },
  {
    canonicalName: GameIds.CURIOSO_CROSSING,
    categories: [GameCategories.EARLY_DECODING, GameCategories.ADVANCED_DECODING],
    studentSkill: 'Irregular Words',
    studentDescription: 'Practice accurate and automatic word recognition by identifying the correct spoken word to guide their Curioso.',
    excludeOnIpad: false,
    release: { [PRODUCT_LECTURA]: ALL_ENVS },
  },
  {
    canonicalName: GameIds.CURIOSO_CALLING,
    categories: [GameCategories.EARLY_DECODING, GameCategories.ADVANCED_DECODING],
    studentSkill: 'Irregular Words',
    studentDescription: 'Practice accurate and automatic word recognition by identifying the correct spoken word to guide their Curioso.',
    excludeOnIpad: false,
    isPrototype: true,
  },
  {
    canonicalName: GameIds.CURIOUS_CASES,
    categories: [GameCategories.KEY_IDEAS_AND_DETAILS],
    studentSkill: 'Key Ideas & Details',
    studentDescription: 'Practice gathering and analyzing text evidence by reading letters, diary entries, and other writings while clues from the text.',
    fieldTrialOnly: true,
  },
  {
    canonicalName: GameIds.CUT_IT_OUT,
    categories: [GameCategories.PHONOLOGICAL_AWARENESS],
    studentSkill: 'Phoneme Isolation',
    studentDescription: 'Listen to a beginning, middle, or ending sound and choose a picture of the word containing the sound in that position.',
    release: { [PRODUCT_LECTURA]: ALL_ENVS },
  },
  {
    canonicalName: GameIds.DEBATE_A_BALL,
    categories: [GameCategories.INTEGRATION_OF_KNOWLEDGE_AND_IDEAS],
    studentSkill: 'Claims with Evidence',
    studentDescription: 'Identify factual evidence strongly related to claims to compete in debates on familiar topics. ',
  },
  {
    canonicalName: GameIds.DIRECTORS_CUT,
    categories: [GameCategories.MICROCOMPREHENSION],
    studentSkill: 'Mental Model Building',
    studentDescription: 'Create a storyboard by understanding and drawing inferences from text to determine which characters, props, and thought bubbles.',
    fieldTrialOnly: true,
  },
  {
    canonicalName: GameIds.ESTA_O_AQUELLA,
    studentSkill: 'Phonics',
    studentDescription: 'Practice reading and hearing words with similar letter sounds.',
    release: {
      [PRODUCT_LECTURA]: ALL_ENVS,
      [PRODUCT_READING]: [],
    },
    categoriesByProduct: {
      [PRODUCT_READING]: [],
      [PRODUCT_LECTURA]: [GameCategories.ADVANCED_DECODING],
    },
  },
  {
    canonicalName: GameIds.FIELD_OBSERVER,
    categories: [GameCategories.KEY_IDEAS_AND_DETAILS],
    studentSkill: 'Evidence for Inferences',
    studentDescription: 'Make and defend inferences about Curiosos hiding in the wild based on what they\'ve read in a field guide.',
  },
  {
    canonicalName: GameIds.FOOD_TRUCK,
    categories: [GameCategories.EARLY_DECODING],
    studentSkill: 'Early Decoding',
    studentDescription: 'Practice "chopping" blends, ending sounds, and whole words into beginning sounds, ending sounds, and individual letters.',
  },
  {
    canonicalName: GameIds.GEM_NYE_FRIENDS,
    categories: [GameCategories.PHONOLOGICAL_AWARENESS],
    studentSkill: 'Blending',
    studentDescription: 'Blend sounds into words to identify the picture of the word the "soundbots" say when blended together.',
    release: { [PRODUCT_LECTURA]: ALL_ENVS },
  },
  {
    canonicalName: GameIds.FOOD_TRUCK_CHEFS_SPECIALS,
    categories: [GameCategories.ADVANCED_DECODING],
    studentSkill: 'Multisyllabic Decoding',
    studentDescription: 'Practice letter-sound correspondence and morphology by chopping up multisyllabic words and using them to build new multisyllabic words.',
    fieldTrialOnly: true,
  },
  {
    canonicalName: GameIds.FOTO_FOTO_COGNADO,
    categories: [GameCategories.VOCABULARY],
    studentSkill: 'Identifying cognates',
    studentDescription: 'Identify cognates and apply understanding of them to complete sentences',
    release: {
      [PRODUCT_LECTURA]: ALL_ENVS,
      [PRODUCT_READING]: [],
    },
  },
  {
    canonicalName: GameIds.GRUMPY_GOBLINS,
    studentSkill: 'Letter Combinations',
    studentDescription: 'Learn sound-spelling correspondences for consonant digraphs and vowel teams by matching their sound to the corresponding letter or combination.',
    release: { [PRODUCT_LECTURA]: ALL_ENVS },
    categoriesByProduct: {
      [PRODUCT_READING]: [GameCategories.LETTER_COMBINATIONS, GameCategories.LETTER_SOUND_CORRESPONDENCE],
      [PRODUCT_LECTURA]: [GameCategories.LETTER_COMBINATIONS],
    },
  },
  {
    canonicalName: GameIds.HANGRY_GOBLINS,
    categories: [GameCategories.LETTER_COMBINATIONS, GameCategories.LETTER_SOUND_CORRESPONDENCE],
    studentSkill: 'Phonics',
    studentDescription: 'Practice letter-sound combinations by feeding individual letter sounds, consonant digraphs, blends, and vowel teams to goblins.',
    release: { [PRODUCT_LECTURA]: ALL_ENVS },
  },
  {
    canonicalName: GameIds.IDIOMATICA,
    categories: [GameCategories.VOCABULARY],
    studentSkill: 'Idioms',
    studentDescription: 'Practice identifying, defining, and applying idioms by answering riddles.',
    supportedInQuests: false,
    fieldTrialOnly: true,
  },
  {
    canonicalName: GameIds.INKBLOTT,
    categories: [GameCategories.VOCABULARY],
    studentSkill: 'Affixes & Roots',
    studentDescription: 'Practice strategies for decoding unfamiliar words by fixing broken sentences using morphemes mined from root words.',
    release: {
      [PRODUCT_LECTURA]: ALL_ENVS,
    },
  },
  {
    canonicalName: GameIds.JUEGA_Y_CONJUGA,
    categories: [GameCategories.MICROCOMPREHENSION],
    studentSkill: 'Comprehension Processes',
    studentDescription: 'Conjugate verbs and identify pronouns for conjugated verbs',
    release: {
      [PRODUCT_LECTURA]: ALL_ENVS,
      [PRODUCT_READING]: [],
    },
  },
  {
    canonicalName: GameIds.MIND_THE_GAP,
    categories: [],
    studentSkill: 'Cloze',
    studentDescription: 'Student assessment',
    release: { [PRODUCT_LECTURA]: [], [PRODUCT_READING]: [] },
  },
  {
    canonicalName: GameIds.MESSAGE_IN_A_BOTTLE,
    categories: [GameCategories.MICROCOMPREHENSION],
    studentSkill: 'Syntactic Awareness',
    studentDescription: 'Build awareness of syntax and the impact word order has on meaning by unscrambling sentences.',
    release: { [PRODUCT_LECTURA]: ALL_ENVS },
  },
  {
    canonicalName: GameIds.NEWSFLASH,
    categories: [GameCategories.KEY_IDEAS_AND_DETAILS],
    studentSkill: 'Plot Structure',
    studentDescription: 'Practice gathering and assembling story elements by identifying the key characters, settings and situations in first-hand accounts.',
    fieldTrialOnly: true,
  },
  {
    canonicalName: GameIds.BOOST_FLUENCY,
    categories: [GameCategories.FLUENCY],
    studentSkill: 'Fluency',
    studentDescription: 'Practice reading grade-level texts with accuracy and fluency by reading passages aloud and answering a comprehension question.',
    release: { [PRODUCT_LECTURA]: [], [PRODUCT_READING]: [] },
  },
  {
    canonicalName: GameIds.PARROT_THEATER,
    categories: [GameCategories.FLUENCY],
    studentSkill: 'Fluency',
    studentDescription: 'Fluency prototype', // TODO: UPDATE WHEN DESCRIPTION IS PROVIDED
    isPrototype: true,
  },
  {
    canonicalName: GameIds.PICKY_GOBLINS,
    categories: [GameCategories.LETTER_SOUND_CORRESPONDENCE],
    studentSkill: 'Phonics',
    studentDescription: 'Practice sound-spelling correspondences for individual letters by listening to a sound from and matching it with the corresponding letter.',
    release: { [PRODUCT_LECTURA]: ALL_ENVS },
  },
  {
    canonicalName: GameIds.PICTURE_THIS,
    categories: [GameCategories.KEY_IDEAS_AND_DETAILS],
    studentSkill: 'Story Elements/Plot',
    studentDescription: 'Help illustrate a story by identifying words that describe its setting, characters, problems, and solutions.',
    excludeOnIpad: false,
  },
  {
    canonicalName: GameIds.PLANTORRES,
    studentSkill: 'Early Decoding',
    studentDescription: 'Identify and manipulate beginning, middle, and ending syllables to assemble word chains.',
    release: {
      [PRODUCT_LECTURA]: ALL_ENVS,
      [PRODUCT_READING]: [],
    },
    categoriesByProduct: {
      [PRODUCT_READING]: [GameCategories.EARLY_DECODING, GameCategories.ADVANCED_DECODING],
      [PRODUCT_LECTURA]: [GameCategories.EARLY_DECODING],
    },
  },
  {
    canonicalName: GameIds.POETS_PATH,
    categories: [GameCategories.CRAFT_AND_STRUCTURE],
    studentSkill: 'Poetry',
    studentDescription: 'Practice poetic devices by identifying words that convey the correct meaning and usage of each device to complete actual poems.',
  },
  {
    canonicalName: GameIds.PUNCHLINE,
    categories: [GameCategories.VOCABULARY],
    studentSkill: 'Multiple Meaning Words',
    studentDescription: 'Learn how words can have multiple meanings by channeling your inner comedian to crack homonym-based jokes.',
  },
  {
    canonicalName: GameIds.READ_ALL_ABOUT_IT,
    categories: [GameCategories.EARLY_DECODING, GameCategories.ADVANCED_DECODING],
    studentSkill: 'Phonics ',
    studentDescription: 'Practice the sound-spelling correspondences, word features, and phonics rules by deciding if image and sentence pairs match the same idea.',
    excludeOnNative: true,
    release: { [PRODUCT_LECTURA]: ALL_ENVS },
  },
  {
    canonicalName: GameIds.RHYME_TIME,
    categories: [GameCategories.EARLY_DECODING],
    studentSkill: 'Early Decoding',
    studentDescription: 'Practice with different rime families by swapping the first letter sounds of words while the ending sounds remain constant.',
    release: { [PRODUCT_LECTURA]: ALL_ENVS },
  },
  {
    canonicalName: GameIds.SHADES_OF_MEANING,
    categories: [GameCategories.VOCABULARY],
    studentSkill: 'Shades of Meaning',
    studentDescription: 'Identify the meaning in similar words, first by ordering them from weakest to strongest, largest to smallest, or least to greatest.',
  },
  {
    canonicalName: GameIds.SHOW_OFF,
    categories: [GameCategories.MICROCOMPREHENSION],
    studentSkill: 'Text Structure',
    studentDescription: 'Learn how text structures give clues to meaning, using cues from pictures to put sentences in the correct order.',
    release: {
      [PRODUCT_LECTURA]: ALL_ENVS,
    },
  },
  {
    canonicalName: GameIds.SILABALON,
    studentSkill: 'Letter Combinations',
    studentDescription: 'Learn sound-spelling correspondences for consonant digraphs and vowel teams by matching their sound to the corresponding letter or combination.',
    release: {
      [PRODUCT_LECTURA]: ALL_ENVS,
      [PRODUCT_READING]: [],
    },
    categoriesByProduct: {
      [PRODUCT_READING]: [],
      [PRODUCT_LECTURA]: [GameCategories.EARLY_DECODING],
    },
  },
  {
    canonicalName: GameIds.SILABALON_LA_COPA,
    studentSkill: 'Phonics', // Should it be Phonics?
    studentDescription: 'Practice letter-sound combinations by feeding individual letter sounds, consonant digraphs, blends, and vowel teams to goblins.', // TODO: UPDATE WHEN DESCRIPTION IS PROVIDED
    release: {
      [PRODUCT_LECTURA]: ALL_ENVS,
      [PRODUCT_READING]: [],
    },
    categoriesByProduct: {
      [PRODUCT_READING]: [],
      [PRODUCT_LECTURA]: [GameCategories.EARLY_DECODING],
    },
  },
  {
    canonicalName: GameIds.SLOPPY_SCROLLS,
    categories: [GameCategories.MICROCOMPREHENSION],
    studentSkill: 'Comprehension Monitoring',
    studentDescription: 'Identify the inconsistencies in sentences that don’t match the rest of the passage. ',
  },
  {
    canonicalName: GameIds.SORT_IT_OUT,
    studentSkill: 'Phonics',
    studentDescription: 'Practice reading words with the hard and soft sounds for the letters g and c.',
    release: { [PRODUCT_LECTURA]: ALL_ENVS },
    categoriesByProduct: {
      [PRODUCT_READING]: [GameCategories.PHONOLOGICAL_AWARENESS, GameCategories.EARLY_DECODING],
      [PRODUCT_LECTURA]: [GameCategories.EARLY_DECODING],
    },
  },
  {
    canonicalName: GameIds.STICKER_BOOK,
    categories: [GameCategories.VOCABULARY],
    studentSkill: 'Categories & Word Relationships',
    studentDescription: 'Develop a contextual understanding of word relationships by placing vocabulary word stickers in their catagory within a sticker book.',
  },
  {
    canonicalName: GameIds.STORYLAB,
    categories: [GameCategories.FLUENCY],
    studentSkill: 'Fluency',
    studentDescription: 'Practice reading grade-level texts with accuracy and fluency by reading with text scrolling automatically at a set speed.',
    supportedInQuests: true,
    fieldTrialOnly: true,
  },
  {
    canonicalName: GameIds.CARTOON_FACTORY,
    categories: [GameCategories.FLUENCY],
    studentSkill: 'Fluency',
    studentDescription: 'Practice reading grade-level texts with accuracy and fluency by voicing a cartoon.',
    supportedInQuests: true,
    fieldTrialOnly: true,
    isPrototype: true,
  },
  {
    canonicalName: GameIds.STORYBOARD,
    categories: [GameCategories.MICROCOMPREHENSION],
    studentSkill: 'Inference',
    studentDescription: 'Make inferences by completing a storyboard that combines relevant background knowledge missing from a given sentence.',
    release: { [PRODUCT_LECTURA]: ALL_ENVS },
  },
  {
    canonicalName: GameIds.STORYBOX,
    categories: [GameCategories.KEY_IDEAS_AND_DETAILS],
    studentSkill: 'Story Elements/Plot',
    studentDescription: 'Help complete a stories by choosing settings, situations, and solutions to send characters on adventures.',
  },
  {
    canonicalName: GameIds.SUPER_MATCH,
    categories: [GameCategories.MICROCOMPREHENSION],
    studentSkill: 'Cognitive Flexibility',
    studentDescription: 'Practice developing cognitive flexibility by matching pictures and words across multiple dimensions .',
  },
  {
    canonicalName: GameIds.TONGUE_TWIST,
    categories: [GameCategories.EARLY_DECODING],
    studentSkill: 'Early Decoding',
    studentDescription: 'Building words by changing the ending sound while the beginning sounds, consonant blends, and consonant digraphs remain constant.',
    release: {
      [PRODUCT_LECTURA]: ALL_ENVS,
    },
  },
  {
    canonicalName: GameIds.TUBE_TALES,
    categories: [GameCategories.CRAFT_AND_STRUCTURE],
    studentSkill: 'Text Schema',
    studentDescription: 'Learn the attributes of different genres and practice identifying them in brief texts.',
  },
  {
    canonicalName: GameIds.UNMASK_THAT,
    categories: [GameCategories.MICROCOMPREHENSION],
    studentSkill: 'Anaphora',
    studentDescription: 'Build an understanding of anaphora, a tool authors use to avoid repetition, by linking pronouns and nouns to their antecedents in text.',
  },
  {
    canonicalName: GameIds.WHATS_THE_BIG_IDEA,
    categories: [GameCategories.KEY_IDEAS_AND_DETAILS],
    studentSkill: 'Main Idea',
    studentDescription: 'Examine pictures, sequences, and short passages to practice identifying the main idea and story details.',
  },
  {
    canonicalName: GameIds.WILD_PLANET,
    categories: [GameCategories.KEY_IDEAS_AND_DETAILS],
    studentSkill: 'Identify Main Idea',
    studentDescription: 'Practice finding the main idea and supporting details in non-fiction texts about science and history.',
    fieldTrialOnly: true,
  },
  {
    canonicalName: GameIds.WORD_CITY,
    studentSkill: 'Early Decoding',
    studentDescription: 'Identify and manipulate beginning, middle, and ending letter sounds to assemble word chains.',
    release: { [PRODUCT_LECTURA]: ALL_ENVS },
    categoriesByProduct: {
      [PRODUCT_READING]: [GameCategories.EARLY_DECODING, GameCategories.ADVANCED_DECODING],
      [PRODUCT_LECTURA]: [GameCategories.EARLY_DECODING],
    },
  },
  {
    canonicalName: GameIds.WORD_RAIDERS,
    categories: [GameCategories.VOCABULARY],
    studentSkill: 'Vocabulary',
    studentDescription: 'Learn grade-appropriate words, while finding synonyms and antonyms for each, to use them in context, and to complete cloze sentences. ',
  },
  {
    canonicalName: GameIds.WORD_SLIDE,
    studentSkill: 'Advanced Decoding',
    studentDescription: 'Practice reading whole words with specific phonics rules by sliding word part and letter tiles to build words.',
    release: {
      [PRODUCT_READING]: ALL_ENVS,
      [PRODUCT_LECTURA]: ALL_ENVS,
    },
    categoriesByProduct: {
      [PRODUCT_READING]: [GameCategories.ADVANCED_DECODING, GameCategories.EARLY_DECODING],
      [PRODUCT_LECTURA]: [GameCategories.EARLY_DECODING],
    },
  },
  {
    canonicalName: GameIds.WORDBOTS,
    categories: [GameCategories.PHONOLOGICAL_AWARENESS],
    studentSkill: 'Segmenting Words',
    studentDescription: 'Learn to segment words into their onsets and rimes to determine which Startbots and Endbots form a word.',
    release: {
      [PRODUCT_LECTURA]: ALL_ENVS,
    },
  },
  {
    canonicalName: GameIds.YEARBOOK,
    categories: [GameCategories.KEY_IDEAS_AND_DETAILS],
    studentSkill: 'Identify Character Traits',
    studentDescription: 'Practice identifying character traits based on text evidence by reading notes, letters, and text artifacts.',
    fieldTrialOnly: true,
  },
  {
    canonicalName: GameIds.ZOOM_BOOM,
    categories: [GameCategories.PHONOLOGICAL_AWARENESS],
    studentSkill: 'Rhyming',
    studentDescription: 'Practice rhyming by listening to a word and identifying the picture of a word that rhymes with it.',
    release: { [PRODUCT_LECTURA]: ALL_ENVS },
  },
  {
    canonicalName: GameIds.E_READER,
    categories: [],
    release: {
      [PRODUCT_READING]: [],
    },
  },
  {
    canonicalName: measureIds.MIND_THE_GAP,
    skillScanName: GameIds.MIND_THE_GAP,
    categories: [],
    release: {
      [PRODUCT_READING]: [],
      [PRODUCT_LECTURA]: [],
    },
    isMeasure: {
      [PRODUCT_READING]: true,
      [PRODUCT_LECTURA]: true,
    },
  },
  {
    canonicalName: measureIds.REAL_WORD_READING,
    skillScanName: GameIds.REAL_WORD_READING,
    categories: [],
    release: {
      [PRODUCT_READING]: [],
      [PRODUCT_LECTURA]: [],
    },
    isMeasure: {
      [PRODUCT_READING]: true,
      [PRODUCT_LECTURA]: true,
    },
  },
  {
    canonicalName: measureIds.LETTER_SOUNDS,
    categories: [],
    release: {
      [PRODUCT_READING]: [],
      [PRODUCT_LECTURA]: [],
    },
    isMeasure: {
      [PRODUCT_READING]: true,
      [PRODUCT_LECTURA]: false,
    },
  },
  {
    canonicalName: measureIds.SYLLABLE_DECODING,
    skillScanName: GameIds.SYLLABLE_DECODING,
    categories: [],
    release: {
      [PRODUCT_READING]: [],
      [PRODUCT_LECTURA]: [],
    },
    isMeasure: {
      [PRODUCT_READING]: false,
      [PRODUCT_LECTURA]: true,
    },
  },
  {
    canonicalName: GameIds.LA_RANA_BRINCACHARCOS,
    categories: [GameCategories.PHONOLOGICAL_AWARENESS],
    studentSkill: 'Syllable segmentation',
    studentDescription: 'Identify and select the quantity of syllables present in a word',
    release: { [PRODUCT_LECTURA]: ALL_ENVS },
  },
  {
    canonicalName: measureIds.BLENDING,
    categories: [],
    release: {
      [PRODUCT_READING]: [],
      [PRODUCT_LECTURA]: [],
    },
    isMeasure: {
      [PRODUCT_READING]: false,
      [PRODUCT_LECTURA]: true,
    },
  },
  {
    canonicalName: measureIds.LETTER_SOUND_CORRESPONDENCES,
    skillScanName: GameIds.LETTER_SOUND_CORRESPONDENCES,
    categories: [],
    release: {
      [PRODUCT_READING]: [],
      [PRODUCT_LECTURA]: [],
    },
    isMeasure: {
      [PRODUCT_READING]: false,
      [PRODUCT_LECTURA]: true,
    },
  },
  {
    canonicalName: measureIds.SEGMENTING,
    categories: [],
    release: {
      [PRODUCT_READING]: [],
      [PRODUCT_LECTURA]: [],
    },
    isMeasure: {
      [PRODUCT_READING]: false,
      [PRODUCT_LECTURA]: true,
    },
  },
  /** NON-USER-FACING ACTIVITIES AFTER THIS POINT */
  {
    canonicalName: GameIds.ROBOVOICE_PROTOTYPE,
    categories: [GameCategories.KEY_IDEAS_AND_DETAILS],
    studentSkill: '',
    studentDescription: '',
    isPrototype: true,
  },
];

// make a new array that adds default values to GameList objects
const gamesListWithDefaults = gamesList.map(({ categories, categoriesByProduct, ...gameDetails }) => {
  const baseCategories = {
    [PRODUCT_READING]: [...(categoriesByProduct?.[PRODUCT_READING] || categories), GameCategories.ALL_GAMES],
    [PRODUCT_LECTURA]: [...(categoriesByProduct?.[PRODUCT_LECTURA] || categories), GameCategories.ALL_GAMES],
  };

  // Add PROTOTYPES category to prototype games
  if (gameDetails.isPrototype) {
    baseCategories[PRODUCT_READING].push(GameCategories.PROTOTYPES);
    baseCategories[PRODUCT_LECTURA].push(GameCategories.PROTOTYPES);
  }

  return {
    ...GAME_DEFAULTS,
    ...gameDetails,
    displayName: localizedStrings.gameDisplayNames[gameDetails.canonicalName],
    categories: baseCategories,
    release: { ...GAME_DEFAULTS.release, ...gameDetails.release },
    isMeasure: { ...GAME_DEFAULTS.isMeasure, ...gameDetails.isMeasure },
  };
});

export default gamesListWithDefaults;
